import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import { rememberPendingLanguage, uiLang, useAuthStore } from '@entities/User';
import { trackGoal } from '@shared/analytics';
import { paths } from '@shared/config';
import { clearAttribution } from '@shared/lib/attribution';
import { completeOAuthLogin, type OAuthSession } from '../api/oauthApi';

type Status = 'pending' | 'error';

export function OAuthSuccessContent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  const setStage = useAuthStore((s) => s.setStage);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);

  const [searchParams] = useSearchParams();
  // ?new=1 бэкенд ставит, когда аккаунт создан этим входом (B).
  const [isNewUser] = useState(() => searchParams.get('new') === '1');
  const [status, setStatus] = useState<Status>('pending');
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    (async () => {
      try {
        const session: OAuthSession = await completeOAuthLogin();
        qc.clear();
        setAccessToken(session.accessToken);
        setUser(session.user);
        // Новый аккаунт: профиль создан с русским по умолчанию — язык этого браузера главнее (B4).
        if (isNewUser) rememberPendingLanguage(uiLang(i18n.resolvedLanguage || i18n.language), session.user.id);
        setStage('onboarding');
        // Регистрация через Яндекс — та же цель, что почтой (иначе она в аналитике не видна);
        // вернувшийся пользователь — вход.
        if (isNewUser) trackGoal('registration', { method: 'yandex' });
        else trackGoal('oauth_login');
        // Метки нового пользователя бэкенд уже записал из query входа; у вернувшегося они не нужны.
        clearAttribution();
        navigate(paths.onboarding, { replace: true });
      } catch {
        setStatus('error');
      }
    })();
  }, [qc, setAccessToken, setUser, setStage, navigate, isNewUser, i18n]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      {status === 'pending' && (
        <motion.div
          key="pending"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.28 }}
          className="flex max-w-[400px] flex-col items-center gap-[18px] text-center"
        >
          <span
            aria-hidden
            className="block h-12 w-12 animate-spin rounded-full border-2 border-line border-t-accent"
          />
          <h1 className="font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[36px]">
            {t('auth.oauth.pending.title')}
          </h1>
          <div className="text-[14px] leading-[1.6] text-muted">{t('auth.oauth.pending.sub')}</div>
        </motion.div>
      )}

      {status === 'error' && (
        <motion.div
          key="error"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.32 }}
          className="flex max-w-[400px] flex-col items-center gap-[18px] text-center"
        >
          <motion.div
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, type: 'spring', stiffness: 240, damping: 16 }}
            className="inline-flex h-16 w-16 items-center justify-center rounded-[20px] bg-danger/15 text-3xl text-danger"
          >
            ✕
          </motion.div>

          <h1 className="font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[36px]">
            {t('auth.oauth.error.title')}
          </h1>
          <div className="text-[14px] leading-[1.6] text-muted">
            {t('auth.oauth.error.OAUTH_REFRESH_FAILED')}
          </div>

          <Link
            to={paths.auth}
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-pill border border-line bg-transparent px-7 py-4 text-[15px] font-medium tracking-[0.02em] text-fg transition-colors hover:bg-elev"
          >
            {t('auth.oauth.error.back')}
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
